import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { t } from './translations';

// ─── GLOW WALL — Public Kindness Gallery ─────────────────────────────────────
// Shows opt-in public glows: beautiful anonymous messages from around the world.
// Users can opt glows in via "Share to Glow Wall" in the message modal.
// Stored in messages.public = true

function Particles() {
  const ps = Array.from({length:6},(_,i)=>({id:i,size:35+Math.random()*60,left:Math.random()*100,delay:Math.random()*8,dur:12+Math.random()*10}));
  return (
    <div className="bg-particles">
      {ps.map(p=><div key={p.id} className="particle" style={{width:p.size,height:p.size,left:`${p.left}%`,animationDelay:`${p.delay}s`,animationDuration:`${p.dur}s`}}/>)}
    </div>
  );
}

const CARD_COLORS = [
  {bg:'rgba(255,209,102,0.1)',border:'rgba(255,209,102,0.25)'},
  {bg:'rgba(255,143,171,0.1)',border:'rgba(255,143,171,0.25)'},
  {bg:'rgba(45,212,191,0.09)',border:'rgba(45,212,191,0.22)'},
  {bg:'rgba(212,160,255,0.1)',border:'rgba(212,160,255,0.22)'},
  {bg:'rgba(255,183,71,0.1)',border:'rgba(255,183,71,0.22)'},
];

export default function GlowWallPage({ lang, setLang, user, onLogout }) {
  const T = t[lang];
  const navigate = useNavigate();
  const [glows, setGlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PER_PAGE = 20;

  const load = useCallback(async (pg=0) => {
    setLoading(true);
    const { data } = await supabase
      .from('messages')
      .select('id,text,emoji,created_at')
      .eq('public', true)
      .eq('reported', false)
      .order('created_at', { ascending: false })
      .range(pg * PER_PAGE, (pg+1) * PER_PAGE - 1);

    if (pg === 0) setGlows(data || []);
    else setGlows(prev => [...prev, ...(data||[])]);
    setHasMore((data||[]).length === PER_PAGE);
    setLoading(false);
  }, []);

  useEffect(() => { load(0); }, [load]);

  function loadMore() { const next = page+1; setPage(next); load(next); }

  return (
    <><Particles/>
    <div className="page" dir={T.dir} style={{fontFamily:T.font}}>

      {/* Nav */}
      <nav dir={T.dir}>
        <div style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}} onClick={()=>navigate('/')}>
          <span style={{fontSize:'1.1rem'}}>←</span>
          <span style={{fontSize:'0.85rem',color:'var(--text-muted)'}}>{lang==='ar'?'الرئيسية':'Home'}</span>
        </div>
        <div style={{fontFamily:T.displayFont,fontSize:'1rem',fontWeight:600,background:'linear-gradient(135deg,#FFE580,#FFD166)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
          {lang==='ar'?'جدار الأضواء':'Glow Wall'}
        </div>
        <button className="lang-btn" onClick={()=>setLang(l=>l==='en'?'ar':'en')}>{lang==='en'?'عربي':'EN'}</button>
      </nav>

      <div style={{maxWidth:760,margin:'0 auto',padding:'36px 20px'}}>
        {/* Header */}
        <div style={{textAlign:'center',marginBottom:40}}>
          <div style={{fontSize:'3rem',marginBottom:16,filter:'drop-shadow(0 0 16px rgba(255,209,102,0.5))'}}>🌍</div>
          <h2 style={{fontFamily:T.displayFont,fontSize:'clamp(1.5rem,4vw,2.1rem)',marginBottom:12,background:'linear-gradient(135deg,#FFE580,#FFD166)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
            {lang==='ar'?'جدار الأضواء 🫙':'The Glow Wall 🫙'}
          </h2>
          <p style={{color:'var(--text-muted)',maxWidth:420,margin:'0 auto',lineHeight:1.75,fontSize:'0.9rem'}}>
            {lang==='ar'
              ?'رسايل حقيقية من ناس حقيقية — بدون أسامي، بس حب خالص'
              :'Real messages from real people — no names, just pure kindness shared with the world'}
          </p>
        </div>

        {/* Glows grid */}
        {glows.length === 0 && !loading ? (
          <div style={{textAlign:'center',padding:'60px 20px',color:'var(--text-muted)'}}>
            <div style={{fontSize:'3rem',marginBottom:16,opacity:0.4}}>🌱</div>
            <div style={{fontSize:'1.1rem',marginBottom:8}}>
              {lang==='ar'?'الجدار لسه فاضي':'The wall is still empty'}
            </div>
            <p style={{fontSize:'0.85rem'}}>
              {lang==='ar'?'ابعت أضواء وشاركها عشان تملي الجدار':'Send glows and share them here to fill the wall'}
            </p>
          </div>
        ) : (
          <div style={{columns:'2 300px',columnGap:14,marginBottom:28}}>
            {glows.map((g,i)=>{
              const col = CARD_COLORS[i % CARD_COLORS.length];
              return (
                <div key={g.id} style={{breakInside:'avoid',marginBottom:14,background:col.bg,border:`1px solid ${col.border}`,borderRadius:16,padding:'20px 22px',animation:'fadeUp 0.4s ease both',animationDelay:`${(i%10)*0.04}s`}}>
                  <div style={{fontSize:'1.6rem',marginBottom:10}}>{g.emoji}</div>
                  <p style={{fontFamily:T.displayFont,fontStyle:'italic',fontSize:'0.95rem',lineHeight:1.75,color:'var(--text)',margin:'0 0 12px'}}>
                    "{g.text}"
                  </p>
                  <div style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.7rem',color:'var(--text-muted)'}}>
                    <div style={{width:4,height:4,borderRadius:'50%',background:'var(--gold)',opacity:0.5}}/>
                    {lang==='ar'?'ضو مجهول':'anonymous glow'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {loading && (
          <div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}>✨</div>
        )}

        {hasMore && !loading && glows.length > 0 && (
          <div style={{textAlign:'center'}}>
            <button className="btn btn-ghost" onClick={loadMore}>
              {lang==='ar'?'حمّل أكتر':'Load more'}
            </button>
          </div>
        )}

        {!user && (
          <div style={{textAlign:'center',marginTop:40,padding:'28px',background:'var(--card)',border:'1px solid var(--border)',borderRadius:20}}>
            <p style={{color:'var(--text-muted)',marginBottom:16,fontSize:'0.9rem'}}>
              {lang==='ar'?'عايز تبعت أضواء وتشاركها على الجدار؟':'Want to send glows and share them on the wall?'}
            </p>
            <button className="btn btn-glow" onClick={()=>navigate('/signup')}>
              {lang==='ar'?'عمل جرّتي مجاناً ✨':'Create my free jar ✨'}
            </button>
          </div>
        )}
      </div>
    </div></>
  );
}
